import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, Table, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';

const Marge = ({ currentUser }) => {
  const [bills, setBills] = useState([]);
  const [billsPin, setBillsPin] = useState([]); // Factures de l'application pin
  const [products, setProducts] = useState([]);
  const [productsPin, setProductsPin] = useState([]); // Produits de l'application pin
  const [invoices, setInvoices] = useState([]);
  const [technicalSheets, setTechnicalSheets] = useState([]); // Fiches techniques
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    location: '',
    search: '',
    family: '' // Nouveau filtre famille
  });
  
  // Nouveaux états pour gérer le chargement séparément
  const [loadingBase, setLoadingBase] = useState(false);
  const [loadingBills, setLoadingBills] = useState(false);

  // 🔍 Fonction pour extraire l'ID de localisation
  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === "string") return loc;
    if (loc.$oid) return loc.$oid;
    if (loc._id) return loc._id.toString();
    return null;
  };

  // 📊 Charger les données de base (Produits, factures d'achat, etc.) UNE SEULE FOIS
  const fetchBaseData = useCallback(async () => {
    setLoadingBase(true);
    setError('');
    try {
      const token = localStorage.getItem("token");
      const [
        productsRes, 
        invoicesRes, 
        locationsRes,
        productsPinRes,
        sheetsRes
      ] = await Promise.all([
        axios.get("/api/ProductV/getProductV?slim=true", { headers: { Authorised: token } }).catch(e => { console.error("Error ProductV:", e.response?.data || e); throw e; }),
        axios.get("/api/InvoicesV/getInvoices?slim=true").catch(e => { console.error("Error InvoicesV:", e.response?.data || e); throw e; }),
        axios.get("/api/Locations/getAllLocations").catch(e => { console.error("Error Locations:", e.response?.data || e); throw e; }),
        axios.get("/api/Product/getProduct?slim=true", { headers: { Authorised: token } }).catch(e => { console.error("Error Product App2:", e.response?.data || e); throw e; }),
        axios.get("/api/TechnicalSheet/all-sheets?slim=true", { headers: { Authorised: token } }).catch(e => { console.error("Error TechSheets:", e.response?.data || e); throw e; })
      ]);

      setProducts(productsRes.data || []);
      setInvoices(invoicesRes.data || []);
      const filteredLocs = (locationsRes.data || []).filter(loc => {
        const nameLower = (loc.name || "").toLowerCase();
        return !nameLower.includes("family");
      });
      setLocations(filteredLocs);
      setProductsPin(productsPinRes.data || []);
      setTechnicalSheets(sheetsRes.data || []);

      console.log("📦 Données de base chargées.");
    } catch (err) {
      console.error("❌ Erreur chargement base:", err);
      setError('Erreur lors du chargement des données de base');
    } finally {
      setLoadingBase(false);
    }
  }, []);

  // 📊 Charger les factures de vente selon la période
  const fetchBills = useCallback(async (start, end) => {
    setLoadingBills(true);
    try {
      console.time("FetchBills");
      const token = localStorage.getItem("token");
      
      let queryParams = "?slim=true";
      if (start) queryParams += `&startDate=${start}`;
      if (end) queryParams += `&endDate=${end}`;

      const [billsRes, billsPinRes] = await Promise.all([
        axios.get(`/api/BillsValises/getAllBillsV${queryParams}`, { headers: { Authorised: token } }),
        axios.get(`/api/Bills/getAllBills${queryParams}`, { headers: { Authorised: token } })
      ]);

      setBills(billsRes.data || []);
      setBillsPin(billsPinRes.data || []);
      console.timeEnd("FetchBills");
      
      console.log(`📦 Ventes App1 (${start} - ${end}):`, billsRes.data?.length);
      console.log(`📦 Ventes App2 (${start} - ${end}):`, billsPinRes.data?.length);
    } catch (err) {
      console.error("❌ Erreur chargement factures:", err);
    } finally {
      setLoadingBills(false);
    }
  }, []);

  useEffect(() => {
    fetchBaseData();
  }, [fetchBaseData]);

  // Se déclenche quand les dates changent (y compris au premier chargement car elles ont une valeur par défaut)
  useEffect(() => {
    fetchBills(filters.startDate, filters.endDate);
  }, [filters.startDate, filters.endDate, fetchBills]);

  // 🔥 OPTIMISATION: Pré-calculer les prix d'achat dans une Map (O(1) au lieu de O(N))
  const purchasePriceMap = React.useMemo(() => {
    const map = new Map();
    for (const invoice of invoices) {
      if (!invoice.items || !Array.isArray(invoice.items)) continue;
      for (const item of invoice.items) {
        let purchasePriceTTC;
        if (item.pricettc && item.pricettc > 0) {
          purchasePriceTTC = item.pricettc;
        } else {
          const purchasePriceHT = item.price || 0;
          const tvaRate = item.tva || 0;
          purchasePriceTTC = purchasePriceHT * (1 + tvaRate / 100);
        }

        const itemProductId = item.productId?.toString() || item.productV?.toString() || item.productV?._id?.toString();
        const itemSubProductId = item.subProductId?.toString() || item.subProductV?.toString() || item.subProductV?._id?.toString();

        if (itemSubProductId) {
          map.set(`sub_${itemSubProductId}`, purchasePriceTTC);
        }
        if (itemProductId) {
          if (!map.has(`prod_${itemProductId}`)) {
            map.set(`prod_${itemProductId}`, purchasePriceTTC);
          }
        }
      }
    }
    return map;
  }, [invoices]);

  // 🔥 OPTIMISATION: Pré-calculer les produits App 1 dans une Map
  const productMapApp1 = React.useMemo(() => {
    const map = new Map();
    for (const product of products) {
      map.set(`prod_${product._id}`, { ...product, isSub: false });
      if (product.subProductsV && Array.isArray(product.subProductsV)) {
        for (const sub of product.subProductsV) {
          map.set(`sub_${sub._id}`, { 
            ...sub, 
            parentName: product.name, 
            parentId: product._id, 
            parentCategory: product.category,
            isSub: true 
          });
        }
      }
    }
    return map;
  }, [products]);

  const findPurchasePrice = useCallback((productId, subProductId) => {
    if (subProductId) {
      const price = purchasePriceMap.get(`sub_${subProductId.toString()}`);
      if (price !== undefined) return price;
    }
    if (productId) {
      const price = purchasePriceMap.get(`prod_${productId.toString()}`);
      if (price !== undefined) return price;
    }
    return 0;
  }, [purchasePriceMap]);

  // 🔍 Obtenir les détails d'un produit avec le bon prix d'achat TTC (Application 1)
  const getProductDetailsApp1 = useCallback((item) => {
    const itemId = item.id?.toString();
    const prodMatch = productMapApp1.get(`prod_${itemId}`);
    const subMatch = productMapApp1.get(`sub_${itemId}`);

    if (prodMatch && !prodMatch.isSub) {
      let purchasePriceTTC;
      if (prodMatch.category === "SET" && prodMatch.subProductsV?.length > 0) {
        purchasePriceTTC = prodMatch.subProductsV.reduce((total, subProduct) => {
          return total + findPurchasePrice(prodMatch._id, subProduct._id);
        }, 0);
      } else {
        purchasePriceTTC = findPurchasePrice(prodMatch._id, null);
      }
      return {
        _id: prodMatch._id,
        parent: prodMatch.name,
        name: null,
        sellingPrice: item.price || prodMatch.price,
        purchasePrice: purchasePriceTTC,
        localisation: extractLocId(prodMatch.localisation),
        hasSubProduct: false,
        category: prodMatch.category || "Autre",
        isSet: prodMatch.category === "SET",
        type: 'achat'
      };
    }

    if (subMatch && subMatch.isSub) {
      const purchasePriceTTC = findPurchasePrice(subMatch.parentId, subMatch._id);
      return {
        _id: subMatch._id,
        parent: subMatch.parentName,
        name: subMatch.name,
        sellingPrice: item.price || subMatch.price,
        purchasePrice: purchasePriceTTC,
        localisation: extractLocId(subMatch.localisation),
        hasSubProduct: true,
        category: subMatch.parentCategory || "Autre",
        isSet: false,
        type: 'achat'
      };
    }

    return {
      _id: item.id,
      name: item.name || "Produit inconnu",
      parent: null,
      sellingPrice: item.price || 0,
      purchasePrice: 0,
      localisation: null,
      hasSubProduct: false,
      category: "Autre",
      isSet: false,
      type: 'achat'
    };
  }, [productMapApp1, findPurchasePrice]);

  // ==================== LOGIQUE APPLICATION 2 (PRODUCTION) ====================

  // 🔥 OPTIMISATION: Pré-calculer les produits App 2 dans une Map
  const productMapApp2 = React.useMemo(() => {
    const map = new Map();
    for (const product of productsPin) {
      map.set(`prod_${extractLocId(product._id)}`, { ...product, isSub: false });
      if (product.subProducts && Array.isArray(product.subProducts)) {
        for (const sub of product.subProducts) {
          map.set(`sub_${extractLocId(sub._id)}`, { 
            ...sub, 
            parentName: product.name, 
            parentId: product._id, 
            parentCategory: product.category,
            isSub: true 
          });
        }
      }
    }
    return map;
  }, [productsPin]);

  // 🔥 OPTIMISATION: Pré-calculer les fiches techniques dans une Map
  const technicalSheetMap = React.useMemo(() => {
    const map = new Map();
    for (const sheet of technicalSheets) {
      if (sheet.subProduct) map.set(extractLocId(sheet.subProduct), sheet.totalCost);
      if (sheet.product) map.set(extractLocId(sheet.product), sheet.totalCost);
    }
    return map;
  }, [technicalSheets]);

  // 🔍 Obtenir les détails d'un produit (Application 2)
  const getProductDetailsApp2 = useCallback((item) => {
    const itemId = extractLocId(item.id);
    const prodMatch = productMapApp2.get(`prod_${itemId}`);
    const subMatch = productMapApp2.get(`sub_${itemId}`);

    let productName = "Produit inconnu";
    let localisation = null;
    let defaultCost = 0;

    if (subMatch && subMatch.isSub) {
      productName = subMatch.parentName; // ou subMatch.name si vous préférez
      localisation = extractLocId(subMatch.localisation);
      defaultCost = (subMatch.price || 0) * 0.7;
    } else if (prodMatch && !prodMatch.isSub) {
      productName = prodMatch.name;
      localisation = extractLocId(prodMatch.localisation);
    }

    const sheetCost = technicalSheetMap.get(itemId);
    const productionCost = sheetCost !== undefined ? sheetCost : defaultCost;

    let category = "Autre";
    if (subMatch && subMatch.isSub) category = subMatch.parentCategory || "Autre";
    else if (prodMatch && !prodMatch.isSub) category = prodMatch.category || "Autre";

    return {
      _id: item.id,
      parent: productName,
      name: subMatch ? subMatch.name : null,
      sellingPrice: item.price || 0,
      purchasePrice: productionCost,
      localisation: localisation,
      hasSubProduct: false,
      category: category,
      isSet: false,
      type: 'production'
    };
  }, [productMapApp2, technicalSheetMap]);

  // 📈 Calculer les marges avec les bons prix (COMBINÉ)
  const calculateMargins = useCallback(() => {
    const marginsByLocation = {};
    let totalMargin = 0;
    let totalSales = 0;
    let totalCost = 0;

    const filteredBillsApp1 = bills; // Le filtrage par date se fait déjà au niveau de l'API
    const filteredBillsApp2 = billsPin;

    console.time("ProcessingMargins");
    console.log("📊 Factures filtrées App1:", filteredBillsApp1.length);
    console.log("📊 Factures filtrées App2:", filteredBillsApp2.length);

    // Traiter chaque facture de vente (Application 1)
    filteredBillsApp1.forEach(bill => {
      if (bill.status === "cancelled") return;
      bill.items.forEach(item => {
        if (item.isCancelled) return;
        const details = getProductDetailsApp1(item);
        const quantity = item.quantity || 1;
        
        // Vérifier le filtre de famille
        if (filters.family && details.category !== filters.family) {
          return;
        }
        
        // Vérifier le filtre de localisation
        const itemLocId = extractLocId(details.localisation);
        if (filters.location && itemLocId !== filters.location) {
          return;
        }

        // Vérifier le filtre de recherche (nom ou sous-produit)
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const productName = (details.parent || "").toLowerCase();
          const subProductName = (details.name || "").toLowerCase();
          if (!productName.includes(searchLower) && !subProductName.includes(searchLower)) {
            return;
          }
        }

        // Calculs basés sur les prix TTC
        const salesAmount = details.sellingPrice * quantity;
        const costAmount = details.purchasePrice * quantity;
        const margin = salesAmount - costAmount;
        const marginPercentage = salesAmount > 0 ? (margin / salesAmount) * 100 : 0;

        // Regrouper par localisation
        const locationId = itemLocId || 'unknown';
        const locationName = locations.find(loc => extractLocId(loc._id) === locationId)?.name || 'Localisation inconnue';

        if (!marginsByLocation[locationId]) {
          marginsByLocation[locationId] = {
            name: locationName,
            sales: 0,
            cost: 0,
            margin: 0,
            percentage: 0,
            items: []
          };
        }

        marginsByLocation[locationId].sales += salesAmount;
        marginsByLocation[locationId].cost += costAmount;
        marginsByLocation[locationId].margin += margin;
        
        // Ajouter l'item détaillé
        marginsByLocation[locationId].items.push({
          date: bill.date,
          product: details.parent || details.name,
          subProduct: details.name,
          quantity,
          sellingPrice: details.sellingPrice,
          purchasePrice: details.purchasePrice,
          salesAmount,
          costAmount,
          margin,
          marginPercentage,
          type: details.type,
          tableNumber: bill.tableNumber || 'N/A'
        });

        // Totaux généraux
        totalSales += salesAmount;
        totalCost += costAmount;
        totalMargin += margin;
      });
    });

    // Traiter chaque facture de vente (Application 2)
    filteredBillsApp2.forEach(bill => {
      if (bill.isStaffAddition) return;
      bill.items.forEach(item => {
        if (item.isCancelled) return;
        const details = getProductDetailsApp2(item);
        const quantity = item.quantity || 1;
        
        // Vérifier le filtre de famille
        if (filters.family && details.category !== filters.family) {
          return;
        }
        
        // Vérifier le filtre de localisation
        const itemLocId = extractLocId(details.localisation);
        if (filters.location && itemLocId !== filters.location) {
          return;
        }

        // Vérifier le filtre de recherche (nom ou sous-produit)
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const productName = (details.parent || "").toLowerCase();
          const subProductName = (details.name || "").toLowerCase();
          if (!productName.includes(searchLower) && !subProductName.includes(searchLower)) {
            return;
          }
        }

        // Calculs basés sur les coûts de production
        const salesAmount = details.sellingPrice * quantity;
        const costAmount = details.purchasePrice * quantity;
        const margin = salesAmount - costAmount;
        const marginPercentage = salesAmount > 0 ? (margin / salesAmount) * 100 : 0;

        // Regrouper par localisation
        const locationId = itemLocId || 'unknown';
        const locationName = locations.find(loc => extractLocId(loc._id) === locationId)?.name || 'Localisation inconnue';

        if (!marginsByLocation[locationId]) {
          marginsByLocation[locationId] = {
            name: locationName,
            sales: 0,
            cost: 0,
            margin: 0,
            percentage: 0,
            items: []
          };
        }

        marginsByLocation[locationId].sales += salesAmount;
        marginsByLocation[locationId].cost += costAmount;
        marginsByLocation[locationId].margin += margin;
        
        // Ajouter l'item détaillé
        marginsByLocation[locationId].items.push({
          date: bill.date,
          product: details.parent || details.name,
          subProduct: details.name,
          quantity,
          sellingPrice: details.sellingPrice,
          purchasePrice: details.purchasePrice,
          salesAmount,
          costAmount,
          margin,
          marginPercentage,
          type: details.type,
          tableNumber: bill.tableNumber || 'N/A'
        });

        // Totaux généraux
        totalSales += salesAmount;
        totalCost += costAmount;
        totalMargin += margin;
      });
    });

    // Calculer les pourcentages par localisation
    Object.keys(marginsByLocation).forEach(locId => {
      const locData = marginsByLocation[locId];
      locData.percentage = locData.sales > 0 ? (locData.margin / locData.sales) * 100 : 0;
    });

    console.log("📈 Résultats marge combinée:", {
      totalSales,
      totalCost,
      totalMargin,
      locations: Object.keys(marginsByLocation).length
    });

    console.timeEnd("ProcessingMargins");

    return {
      byLocation: marginsByLocation,
      totals: {
        sales: totalSales,
        cost: totalCost,
        margin: totalMargin,
        percentage: totalSales > 0 ? (totalMargin / totalSales) * 100 : 0
      }
    };
  }, [
    bills, 
    billsPin, 
    filters, 
    getProductDetailsApp1, 
    getProductDetailsApp2, 
    locations
  ]);

  const marginsData = React.useMemo(() => calculateMargins(), [calculateMargins]);

  // 📊 Statistiques résumées
  const getStatsSummary = () => {
    const locationsCount = Object.keys(marginsData.byLocation).length;
    const totalItems = Object.values(marginsData.byLocation).reduce(
      (sum, loc) => sum + loc.items.length, 0
    );

    // Calculer les totaux par type
    const totalsByType = { achat: 0, production: 0 };
    Object.values(marginsData.byLocation).forEach(loc => {
      loc.items.forEach(item => {
        if (item.type === 'achat') {
          totalsByType.achat++;
        } else if (item.type === 'production') {
          totalsByType.production++;
        }
      });
    });

    return {
      locationsCount,
      totalItems,
      totalsByType,
      ...marginsData.totals
    };
  };

  const stats = getStatsSummary();

  // Obtenir la liste dynamique des familles
  const availableFamilies = React.useMemo(() => {
    const families = new Set();
    for (const prod of products) {
      if (prod.category) families.add(prod.category);
    }
    for (const prod of productsPin) {
      if (prod.category) families.add(prod.category);
    }
    return Array.from(families).sort();
  }, [products, productsPin]);

  if (loadingBase || loadingBills) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
        <p className="mt-2">
          {loadingBase ? "Chargement des données de base..." : "Chargement des factures..."}
        </p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">📈 État des Marges (Combiné)</h2>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Filtres */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Filtres</h5>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date de début</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date de fin</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Famille (Catégorie)</Form.Label>
                <Form.Select
                  value={filters.family}
                  onChange={(e) => setFilters({...filters, family: e.target.value})}
                >
                  <option value="">Toutes les familles</option>
                  {availableFamilies.map(fam => (
                    <option key={fam} value={fam}>{fam}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Localisation</Form.Label>
                <Form.Select
                  value={filters.location}
                  onChange={(e) => setFilters({...filters, location: e.target.value})}
                >
                  <option value="">Toutes</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={extractLocId(loc._id)}>
                      {loc.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Recherche Produit</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nom du produit..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Résumé général */}
      <Card className="mb-4 bg-light">
        <Card.Body>
          <Row>
            <Col md={3} className="text-center">
              <h6>Chiffre d'Affaires</h6>
              <h4 className="text-primary">{stats.sales.toFixed(2)} DT</h4>
              <small className="text-muted">
                📦 Achat: {stats.totalsByType.achat} ventes
              </small>
              <br />
              <small className="text-muted">
                🏭 Production: {stats.totalsByType.production} ventes
              </small>
            </Col>
            <Col md={3} className="text-center">
              <h6>Coût Total</h6>
              <h4 className="text-danger">{stats.cost.toFixed(2)} DT</h4>
            </Col>
            <Col md={3} className="text-center">
              <h6>Marge Totale</h6>
              <h4 className={stats.margin >= 0 ? "text-success" : "text-danger"}>
                {stats.margin.toFixed(2)} DT
              </h4>
            </Col>
            <Col md={3} className="text-center">
              <h6>Pourcentage Marge</h6>
              <h4 className={stats.percentage >= 0 ? "text-success" : "text-danger"}>
                {stats.percentage.toFixed(1)} %
              </h4>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col className="text-center">
              <small className="text-muted">
                {stats.locationsCount} localisation(s) • {stats.totalItems} ventes totales
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Détails par localisation (GARDER L'AFFICHAGE D'ORIGINE) */}
      {Object.entries(marginsData.byLocation).map(([locId, data]) => (
        <Card key={locId} className="mb-4">
          <Card.Header className="bg-secondary text-white">
            <h5 className="mb-0">
              📍 {data.name}
              <span className="float-end">
                Marge: {data.margin.toFixed(2)} DT ({data.percentage.toFixed(1)}%)
              </span>
            </h5>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Table</th>
                  <th>Type</th>
                  <th>Produit</th>
                  <th>Sous-produit</th>
                  <th>Quantité</th>
                  <th>Prix Vente</th>
                  <th>Coût</th>
                  <th>CA</th>
                  <th>Coût Total</th>
                  <th>Marge</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, index) => (
                  <tr key={index}>
                    <td>{new Date(item.date).toLocaleDateString('fr-FR')}</td>
                    <td>{item.tableNumber}</td>
                    <td>
                      <span className={`badge ${item.type === 'achat' ? 'bg-info' : 'bg-warning'}`}>
                        {item.type === 'achat' ? '📦 Achat' : '🏭 Production'}
                      </span>
                    </td>
                    <td>{item.product}</td>
                    <td>{item.subProduct || '-'}</td>
                    <td>{item.quantity}</td>
                    <td>{item.sellingPrice.toFixed(2)} DT</td>
                    <td>{item.purchasePrice.toFixed(2)} DT</td>
                    <td>{item.salesAmount.toFixed(2)} DT</td>
                    <td>{item.costAmount.toFixed(2)} DT</td>
                    <td className={item.margin >= 0 ? "text-success" : "text-danger"}>
                      {item.margin.toFixed(2)} DT
                    </td>
                    <td className={item.marginPercentage >= 0 ? "text-success" : "text-danger"}>
                      {item.marginPercentage.toFixed(1)}%
                    </td>
                  </tr>
                ))}
                {/* Total de la localisation */}
                <tr className="table-info fw-bold">
                  <td colSpan="8">Total {data.name}</td>
                  <td>{data.sales.toFixed(2)} DT</td>
                  <td>{data.cost.toFixed(2)} DT</td>
                  <td className={data.margin >= 0 ? "text-success" : "text-danger"}>
                    {data.margin.toFixed(2)} DT
                  </td>
                  <td className={data.percentage >= 0 ? "text-success" : "text-danger"}>
                    {data.percentage.toFixed(1)}%
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ))}

      {Object.keys(marginsData.byLocation).length === 0 && !loading && (
        <Alert variant="info" className="text-center">
          Aucune donnée de marge trouvée pour les critères sélectionnés.
        </Alert>
      )}
    </div>
  );
};

export default Marge;